import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { apiService } from "../services/api";
import {
  formatPrice,
  getProductImage,
  getProductUrl,
  truncateText,
} from "../utils/productHelpers";

const SearchPageWrapper = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px 0;

  @media (max-width: 480px) {
    padding: 0px 0;
  }
`;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 480px) {
    padding: 0px 0;
  }
`;

const SearchHeader = styled.div`
  background: white;
  padding: 30px;
  border-radius: 10px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const SearchTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin: 0 0 10px 0;
`;

const SearchSubtitle = styled.p`
  color: #666;
  font-size: 16px;
  margin: 0;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 15px;
  margin-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 20px;
  border: 2px solid #e5e5e5;
  border-radius: 25px;
  font-size: 16px;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: #ff6720;
    box-shadow: 0 0 0 3px rgba(255, 103, 32, 0.1);
  }
`;

const SearchButton = styled.button`
  padding: 12px 30px;
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e55a1c;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
  }
`;

const ResultsSection = styled.div`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    box-shadow: none;
  }
`;

const ResultsHeader = styled.div`
  padding: 20px 30px;
  border-bottom: 1px solid #e5e5e5;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 15px 20px;
  }
`;

const ResultsCount = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
  padding: 30px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
    padding: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 15px;
  }
`;

const ProductCard = styled(Link)`
  display: block;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  height: fit-content;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: #ff6720;
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 220px;
  background: #ffffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding-left: 20px;
  padding-right: 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }

  ${ProductCard}:hover & img {
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const ProductInfo = styled.div`
  padding: 20px;

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const ProductTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 10px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #333;
  min-height: 44px;

  @media (max-width: 480px) {
    line-height: none !important;
    padding: 0 !important;
    min-height: fit-content;
  }
`;

const ProductDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 36px;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  font-size: 16px;
  color: #666;
  background: white;
  border-radius: 10px;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 10px;

  h2 {
    font-size: 24px;
    color: #333;
    margin: 0 0 15px 0;
  }

  p {
    font-size: 16px;
    color: #666;
    margin: 0 0 25px 0;
    line-height: 1.5;
  }
`;

const SuggestionsLink = styled(Link)`
  display: inline-block;
  padding: 12px 25px;
  background: #ff6720;
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #e55a1c;
    transform: translateY(-1px);
  }
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 30px auto 0;
  padding: 12px 30px;
  background: #333;
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #ff6720;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProductActions = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  justify-items: center;
  opacity: 1;
  transition: opacity 0.3s ease;
`;

const ViewButton = styled(Link)`
  display: block;
  width: 100% !important;
  justify-content: center;
  padding: 12px 16px;
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 50px;
  text-decoration: none;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a1c;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 13px;
  }
`;

const ProductsContainer = styled.div`
  display: flex;
  gap: 30px;
  width: 100%;
  padding: 40px 0;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Sidebar = styled.div`
  width: 200px;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 100%;
    display: ${(props) => (props.showFilters ? "block" : "none")};
  }
`;

const FilterSection = styled.div`
  background: #ffffffff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const FilterTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
`;

const FilterOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;

  input {
    margin: 0;
  }

  &:hover {
    color: #333;
  }
`;

const FilterControlsWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: row;
    gap: 10px;
    align-items: stretch;
    justify-content: space-between;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 1px solid #ddd;
  border-radius: 25px;
  background: white;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: none;

  &:hover {
    border-color: #ff6720;
    color: #ff6720;
  }

  &:focus {
    outline: none;
    border-color: #ff6720;
  }

  @media (max-width: 1024px) {
    display: flex;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProductsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px 30px 10px 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
`;

const SortSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #ff6720;
  }
`;

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Add filter-related state
  const [filters, setFilters] = useState({
    productType: [],
    cordedCordless: searchParams.get("type") || "",
    batteryAmpHours: "",
    brand: "",
    category: "",
  });
  const [productTypeSearch, setProductTypeSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");

  // Update page title (unchanged)
  useEffect(() => {
    const query = searchParams.get("q");
    if (query) {
      document.title = `Search: ${query} | XPLOD-GROUPS`;
    } else {
      document.title = "Search Products | XPLOD-GROUPS";
    }
    return () => {
      document.title = "Home | XPLOD-GROUPS";
    };
  }, [searchParams]);

  // Add filter logic functions before performSearch
  const getProductTypesFromProducts = () => {
    const types = new Set();

    results.forEach((product) => {
      if (product.product_type) {
        types.add(product.product_type);
      }
      if (product.category) {
        types.add(product.category);
      }
      if (product.subcategory) {
        types.add(product.subcategory);
      }
      const productName = product.name.toLowerCase().trim();
      const nameParts = productName
        .split(/[\s\-_,|]+/)
        .filter((part) => part.length > 2);
      const toolKeywords = [
        "chainsaw",
        "saw",
        "cutter",
        "trimmer",
        "blower",
        "mower",
        "washer",
        "brushcutter",
        "tiller",
        "tiler",
        "vacuum",
        "cleaner",
        "edger",
        "hedge",
        "leaf",
        "carwash",
        "pressurewasher",
        "water",
        "waterpump",
        "pump",
        "pressure",
        "brush",
        "pole",
        "lopper",
        "shears",
        "pruner",
        "blower",
        "mistblower",
        "sprayer",
        "spray",
        "lawnmower",
      ];
      nameParts.forEach((part) => {
        if (toolKeywords.some((keyword) => part.includes(keyword))) {
          const cleanType = part.charAt(0).toUpperCase() + part.slice(1);
          types.add(cleanType);
        }
      });
      if (
        productName.includes("pole saw") ||
        productName.includes("pole-saw")
      ) {
        types.add("Pole Saw");
      }
      if (
        productName.includes("hedge trimmer") ||
        productName.includes("hedge-trimmer")
      ) {
        types.add("Hedge Trimmer");
      }
      if (
        productName.includes("leaf blower") ||
        productName.includes("leaf-blower")
      ) {
        types.add("Leaf Blower");
      }
      if (
        productName.includes("pressure washer") ||
        productName.includes("pressure-washer")
      ) {
        types.add("Pressure Washer");
      }
      if (
        productName.includes("lawn mower") ||
        productName.includes("lawn-mower")
      ) {
        types.add("Lawn Mower");
      }
      if (
        productName.includes("brush cutter") ||
        productName.includes("brush-cutter")
      ) {
        types.add("Brush Cutter");
      }
    });

    return Array.from(types).sort();
  };

  const productTypes = getProductTypesFromProducts();
  const filteredProductTypes = productTypes.filter((type) =>
    type.toLowerCase().includes(productTypeSearch.toLowerCase()),
  );

  const getProductTypeCount = (type) => {
    return results.filter((product) => {
      const productName = product.name.toLowerCase();
      return (
        (product.product_type &&
          product.product_type.toLowerCase() === type.toLowerCase()) ||
        (product.category &&
          product.category.toLowerCase() === type.toLowerCase()) ||
        (product.subcategory &&
          product.subcategory.toLowerCase() === type.toLowerCase()) ||
        productName === type.toLowerCase() // Strict name match
      );
    }).length;
  };

  const getPowerTypeCount = (type) => {
    return results.filter((product) => {
      if (product.type) {
        const typeValue = product.type.toLowerCase();
        if (
          type === "petrol" &&
          (typeValue.includes("petrol") || typeValue.includes("gas"))
        ) {
          return true;
        }
        if (
          type === "cordless" &&
          (typeValue.includes("cordless") || typeValue.includes("battery"))
        ) {
          return true;
        }
        if (
          type === "corded" &&
          (typeValue.includes("corded") || typeValue.includes("electric"))
        ) {
          return true;
        }
      }
      const searchText =
        `${product.name || ""} ${product.description || ""} ${product.brief_description || ""} ${product.type || ""} ${product.brand || ""}`.toLowerCase();
      const searchTerms = {
        cordless: ["cordless", "battery", "20v", "18v", "12v"],
        corded: ["corded", "electric", "plug-in", "cable"],
        petrol: ["petrol", "gas", "gasoline"],
      };
      return (
        searchTerms[type]?.some((term) => searchText.includes(term)) || false
      );
    }).length;
  };

  const getBatteryAmpHoursCount = (ampHours) => {
    return results.filter((product) => {
      const searchText =
        `${product.name} ${product.description || ""} ${product.brief_description || ""}`.toLowerCase();
      return searchText.includes(ampHours.toLowerCase());
    }).length;
  };

  const getAvailablePowerTypes = () => {
    const powerTypes = new Set();
    results.forEach((product) => {
      if (product.type) {
        const typeValue = product.type.toLowerCase();
        if (typeValue.includes("petrol") || typeValue.includes("gas")) {
          powerTypes.add("petrol");
        }
        if (typeValue.includes("cordless") || typeValue.includes("battery")) {
          powerTypes.add("cordless");
        }
        if (typeValue.includes("corded") || typeValue.includes("electric")) {
          powerTypes.add("corded");
        }
      }
      const searchText =
        `${product.name || ""} ${product.description || ""} ${product.brief_description || ""} ${product.type || ""} ${product.brand || ""}`.toLowerCase();
      const cordlessTerms = ["cordless", "battery", "20v", "18v", "12v"];
      if (cordlessTerms.some((term) => searchText.includes(term))) {
        powerTypes.add("cordless");
      }
      const cordedTerms = ["corded", "electric", "plug-in", "cable"];
      if (cordedTerms.some((term) => searchText.includes(term))) {
        powerTypes.add("corded");
      }
      const petrolTerms = ["petrol", "gas", "gasoline"];
      if (petrolTerms.some((term) => searchText.includes(term))) {
        powerTypes.add("petrol");
      }
    });
    return Array.from(powerTypes).sort();
  };

  const availablePowerTypes = getAvailablePowerTypes();
  const availableBatteryOptions = ["1.5 ah", "2 ah"].filter(
    (option) => getBatteryAmpHoursCount(option) > 0,
  );
  const hasCordlessProducts = getPowerTypeCount("cordless") > 0;

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (filterType === "productType") {
        if (newFilters[filterType].includes(value)) {
          newFilters[filterType] = newFilters[filterType].filter(
            (item) => item !== value,
          );
        } else {
          newFilters[filterType] = [...newFilters[filterType], value];
        }
      } else {
        newFilters[filterType] = newFilters[filterType] === value ? "" : value;
        if (filterType === "cordedCordless") {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            if (value && value !== prev.get("type")) {
              newParams.set("type", value);
            } else {
              newParams.delete("type");
            }
            return newParams;
          });
        }
      }
      return newFilters;
    });
  };

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...results];
    if (filters.productType.length > 0) {
      filtered = filtered.filter((product) =>
        filters.productType.some((type) => {
          const productName = product.name.toLowerCase();
          return (
            (product.product_type &&
              product.product_type.toLowerCase() === type.toLowerCase()) ||
            (product.category &&
              product.category.toLowerCase() === type.toLowerCase()) ||
            (product.subcategory &&
              product.subcategory.toLowerCase() === type.toLowerCase()) ||
            productName === type.toLowerCase()
          );
        }),
      );
    }
    if (filters.cordedCordless) {
      filtered = filtered.filter((product) => {
        if (product.type) {
          const typeValue = product.type.toLowerCase();
          const filterValue = filters.cordedCordless.toLowerCase();
          if (typeValue.includes(filterValue)) {
            return true;
          }
        }
        const searchText =
          `${product.name || ""} ${product.description || ""} ${product.brief_description || ""} ${product.type || ""} ${product.brand || ""}`.toLowerCase();
        const searchTerms = {
          cordless: ["cordless", "battery", "20v", "18v", "12v"],
          corded: ["corded", "electric", "plug-in"],
          petrol: ["petrol", "gas", "gasoline"],
        };
        return (
          searchTerms[filters.cordedCordless]?.some((term) =>
            searchText.includes(term),
          ) || false
        );
      });
    }
    if (filters.batteryAmpHours) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(filters.batteryAmpHours) ||
          (product.description &&
            typeof product.description === "string" &&
            product.description
              .toLowerCase()
              .includes(filters.batteryAmpHours)),
      );
    }
    if (filters.category) {
      filtered = filtered.filter(
        (product) => product.category === filters.category,
      );
    }
    // Calculate relevance based on search query match
    const searchQuery = searchParams.get("q")?.toLowerCase() || "";
    filtered.forEach((product) => {
      const text =
        `${product.name || ""} ${product.description || ""} ${product.brief_description || ""}`.toLowerCase();
      product.relevanceScore = searchQuery
        .split(/\s+/)
        .reduce((score, keyword) => {
          return score + (text.includes(keyword) ? 1 : 0);
        }, 0);
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "relevance":
          return (b.relevanceScore || 0) - (a.relevanceScore || 0);
        case "a-to-z":
          return a.name.localeCompare(b.name);
        case "z-to-a":
          return b.name.localeCompare(a.name);
        default:
          return a.name.localeCompare(b.name);
      }
    });
    return filtered;
  }, [results, filters, sortBy, searchParams]);

  const performSearch = async (query, pageNum = 1, append = false) => {
    if (!query.trim()) {
      setResults([]);
      setTotalResults(0);
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.searchProducts(query, {
        status: "active",
        limit: 12,
        offset: (pageNum - 1) * 12,
      });

      if (response.success) {
        const newResults = response.data || [];
        if (append) {
          setResults((prev) => [...prev, ...newResults]);
        } else {
          setResults(newResults);
        }
        setTotalResults(response.total || newResults.length);
        setHasMore(newResults.length === 12);
      } else {
        if (!append) {
          setResults([]);
          setTotalResults(0);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Search error:", error);
      if (!append) {
        setResults([]);
        setTotalResults(0);
      }
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("q", searchQuery);
        return newParams;
      });
      setPage(1);
      performSearch(searchQuery, 1, false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    performSearch(searchQuery, nextPage, true);
  };

  useEffect(() => {
    const query = searchParams.get("q");
    if (query) {
      setSearchQuery(query);
      performSearch(query, 1, false);
    }
  }, [searchParams]);

  const currentQuery = searchParams.get("q") || "";

  return (
    <SearchPageWrapper>
      <Container>
        <SearchHeader>
          <SearchTitle>
            {currentQuery ? `Search Results` : "Search Products"}
          </SearchTitle>
          <SearchSubtitle>
            {currentQuery
              ? `Found ${totalResults} product${totalResults !== 1 ? "s" : ""} for "${currentQuery}"`
              : "Enter keywords to search our product catalog"}
          </SearchSubtitle>
          <SearchForm onSubmit={handleSearchSubmit}>
            <SearchInput
              type="text"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchButton type="submit">Search</SearchButton>
          </SearchForm>
        </SearchHeader>

        <ProductsContainer>
          <Sidebar showFilters={showFilters}>
            <FilterSection>
              <FilterTitle>Product Type</FilterTitle>
              <div style={{ marginBottom: "15px" }}>
                <input
                  type="text"
                  placeholder="Search Product Type"
                  value={productTypeSearch}
                  onChange={(e) => setProductTypeSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #b3b3b3ff",
                    borderRadius: "50px",
                    fontSize: "14px",
                  }}
                />
              </div>
              <FilterOptions>
                {filteredProductTypes.map((type) => (
                  <FilterOption key={type}>
                    <input
                      type="checkbox"
                      checked={filters.productType.includes(type)}
                      onChange={() => handleFilterChange("productType", type)}
                    />
                    {type} ({getProductTypeCount(type)})
                  </FilterOption>
                ))}
              </FilterOptions>
            </FilterSection>
            {availablePowerTypes.length > 0 && (
              <FilterSection>
                <FilterTitle>Power Type</FilterTitle>
                <FilterOptions>
                  {availablePowerTypes.map((type) => {
                    const displayNames = {
                      corded: "Electric",
                      cordless: "Battery",
                      petrol: "Petrol",
                    };
                    const displayName =
                      displayNames[type] ||
                      type.charAt(0).toUpperCase() + type.slice(1);
                    return (
                      <FilterOption key={type}>
                        <input
                          type="checkbox"
                          checked={filters.cordedCordless === type}
                          onChange={() =>
                            handleFilterChange("cordedCordless", type)
                          }
                        />
                        {displayName} ({getPowerTypeCount(type)})
                      </FilterOption>
                    );
                  })}
                </FilterOptions>
              </FilterSection>
            )}
            {hasCordlessProducts && availableBatteryOptions.length > 0 && (
              <FilterSection>
                <FilterTitle>Battery Amp Hours (Ah)</FilterTitle>
                <FilterOptions>
                  {availableBatteryOptions.map((option) => (
                    <FilterOption key={option}>
                      <input
                        type="checkbox"
                        checked={filters.batteryAmpHours === option}
                        onChange={() =>
                          handleFilterChange("batteryAmpHours", option)
                        }
                      />
                      {option.toUpperCase()} ({getBatteryAmpHoursCount(option)})
                    </FilterOption>
                  ))}
                </FilterOptions>
              </FilterSection>
            )}
          </Sidebar>

          <div style={{ flex: 1 }}>
            {isLoading && page === 1 ? (
              <LoadingState>
                <div>Searching for products...</div>
              </LoadingState>
            ) : filteredAndSortedProducts.length > 0 ? (
              <ResultsSection>
                <ProductsHeader>
                  <ResultsCount>
                    Showing {filteredAndSortedProducts.length} of {totalResults}{" "}
                    products
                  </ResultsCount>
                  <FilterControlsWrapper>
                    <FilterButton onClick={() => setShowFilters(!showFilters)}>
                      <span>Filters</span>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="4" y1="21" x2="4" y2="14" />
                        <line x1="4" y1="10" x2="4" y2="3" />
                        <line x1="12" y1="21" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12" y2="3" />
                        <line x1="20" y1="21" x2="20" y2="16" />
                        <line x1="20" y1="12" x2="20" y2="3" />
                        <line x1="1" y1="14" x2="7" y2="14" />
                        <line x1="9" y1="8" x2="15" y2="8" />
                        <line x1="17" y1="16" x2="23" y2="16" />
                      </svg>
                    </FilterButton>
                    <SortSelect
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="relevance">Relevance</option>
                      <option value="a-to-z">A to Z</option>
                      <option value="z-to-a">Z to A</option>
                    </SortSelect>
                  </FilterControlsWrapper>
                </ProductsHeader>
                <ProductGrid>
                  {filteredAndSortedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      to={`/products/${product.id}`}
                    >
                      <ProductImage>
                        <img
                          src={getProductImage(product)}
                          alt={product.name}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/280x220?text=No+Image";
                          }}
                        />
                      </ProductImage>
                      <ProductInfo>
                        <ProductTitle>{product.name}</ProductTitle>
                        <ProductDescription>
                          {truncateText(
                            product.short_description ||
                              product.brief_description ||
                              product.description ||
                              "No description available",
                            80,
                          )}
                        </ProductDescription>
                        <ProductActions className="product-actions">
                          <ViewButton to={`/products/${product.id}`}>
                            View Details
                          </ViewButton>
                        </ProductActions>
                      </ProductInfo>
                    </ProductCard>
                  ))}
                </ProductGrid>
                {hasMore && (
                  <LoadMoreButton onClick={handleLoadMore} disabled={isLoading}>
                    {isLoading ? "Loading..." : "Load More Products"}
                  </LoadMoreButton>
                )}
              </ResultsSection>
            ) : currentQuery ? (
              <NoResults>
                <h2>No products found</h2>
                <p>
                  We couldn't find any products matching "{currentQuery}". Try
                  using different keywords or browse our categories.
                </p>
                <SuggestionsLink to="/">Browse All Products</SuggestionsLink>
              </NoResults>
            ) : null}
          </div>
        </ProductsContainer>
      </Container>
    </SearchPageWrapper>
  );
};

export default SearchPage;
